import type { AwsConnectionClient } from '../components/aws-import/AwsConnectionEditor'
import { credentialTestBody, ssoApiVia, type AwsSsoConnectionApi, type CredentialTestResult } from './awsSso'
import type { AwsConnectionOutput, AwsConnectionPayload } from './types'

/**
 * API client for the PUBLIC /newcustomer onboarding flow. Deliberately does
 * NOT go through api/core.ts: there is no signed-in user, so no bearer token
 * is attached and a 401 must surface as an inline error (an expired
 * onboarding token), never a redirect to /login.
 *
 * Everything after the initial create carries the signed onboarding token in
 * `X-Onboarding-Token` — the backend scopes each call to the company the
 * token was minted for.
 */

async function publicRequest<T>(path: string, init?: RequestInit, token?: string): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
  }
  if (token) headers['X-Onboarding-Token'] = token
  const resp = await fetch(path, { ...init, headers })
  if (!resp.ok) {
    const body = await resp.text()
    let detail = body
    try {
      detail = JSON.parse(body)?.detail ?? body
    } catch {
      // non-JSON error body — use as-is
    }
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail))
  }
  return resp.json() as Promise<T>
}

export interface StartOnboardingResult {
  slug: string
  token: string
}

export const publicOnboardingApi = {
  /** Create the company from the customer-typed profile; returns the token
   *  that scopes the rest of the flow. */
  start: (companyUrl: string, values: Record<string, unknown>) =>
    publicRequest<StartOnboardingResult>('/api/public/onboarding', {
      method: 'POST',
      body: JSON.stringify({ company_url: companyUrl, values }),
    }),

  /** The `AwsConnectionEditor` client for this onboarding (the slug the
   *  editor passes is ignored — the token picks the company). */
  connectionClient: (token: string): AwsConnectionClient => ({
    saveAwsConnection: (_slug: string, payload: AwsConnectionPayload) =>
      publicRequest<AwsConnectionOutput>(
        '/api/public/onboarding/connection',
        { method: 'PUT', body: JSON.stringify(payload) },
        token,
      ),
    testAwsCredentials: (_slug, accessKey, secretKey, expectedAccountId, sessionToken, awsAccountId) =>
      publicRequest<CredentialTestResult>(
        '/api/public/onboarding/connection/test',
        {
          method: 'POST',
          body: JSON.stringify(
            credentialTestBody(accessKey, secretKey, expectedAccountId, sessionToken, awsAccountId),
          ),
        },
        token,
      ),
  }),

  runScan: (token: string) =>
    publicRequest<{ ok: boolean; status: string }>(
      '/api/public/onboarding/scan',
      { method: 'POST' },
      token,
    ),

  skipAws: (token: string) =>
    publicRequest<{ ok: boolean }>('/api/public/onboarding/skip', { method: 'POST' }, token),

  /** Token-gated mirror of the /api/aws-sso device-flow endpoints. */
  ssoApi: (token: string): AwsSsoConnectionApi =>
    ssoApiVia('/api/public/onboarding/sso', (path, body) =>
      publicRequest(path, { method: 'POST', body: JSON.stringify(body) }, token),
    ),
}
